// SPDX-FileCopyrightText: 2026 Ebiose AI
// SPDX-License-Identifier: LicenseRef-Ebiose-Proprietary
// All rights reserved. Confidential — unauthorized distribution prohibited.

// Top row of the agent detail page matching mockup `Détail Agent.dc.html`.
// `Test` and `Delete` buttons omitted per PRD §Implementation Decisions — those flows don't exist yet.
// Visual values come from the Lagon design system (global.css tokens) — no invented values.
import { useState } from "react"
import { Copy, Download } from "lucide-react"

// No new fetch — serialises from the detail payload already in memory.
function exportAgentModel(agent: AgentDetails) {
  const json = JSON.stringify(agent.agent_model, null, 2)
  const blob = new Blob([json], { type: "application/json" })
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement("a")
  anchor.href = url
  anchor.download = `${agent.agent_id}.json`
  anchor.click()
  URL.revokeObjectURL(url)
}

// aria-label stays "copy"/"copied" so the button is reliably queryable by accessible name.
function CopyChip({ value }: { value: string }) {
  const [copied, setCopied] = useState(false)

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(value)
    } catch {
      // Clipboard refused (permission denied, insecure context) —
      // nothing was copied, so never show the success state.
      return
    }
    setCopied(true)
    setTimeout(() => setCopied(false), 1500)
  }

  return (
    <button
      type="button"
      onClick={onCopy}
      aria-label={copied ? "copied" : "copy"}
      className={
        "inline-flex items-center gap-1.5 rounded-md border px-2 py-px " +
        "transition-colors " +
        (copied
          ? "border-border-active text-primary"
          : "border-border-strong text-text-tertiary hover:border-border-active hover:text-text-secondary")
      }
    >
      <Copy className="h-3 w-3" />
      {copied ? "copied" : "copy"}
    </button>
  )
}

function DotPill({
  label,
  dotClass,
  className,
}: {
  label: string
  dotClass: string
  className: string
}) {
  return (
    <span
      className={
        "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-[3px] " +
        "text-[11.5px] font-medium " +
        className
      }
    >
      <span className={"h-1.5 w-1.5 rounded-full " + dotClass} />
      {label}
    </span>
  )
}

function formatCreated(iso: string): string {
  const d = new Date(iso)
  const pad = (n: number) => String(n).padStart(2, "0")
  return (
    `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}, ` +
    `${pad(d.getHours())}:${pad(d.getMinutes())}`
  )
}

// Performs no I/O of its own beyond the Export JSON download and copy-to-clipboard.
export function AgentHeader({ agent }: { agent: AgentDetails }) {
  const card = agent.agent_model.card
  // Role badge text: the role, or "domain" when role is empty. Model-type
  // badge carries the union discriminator value verbatim.
  const roleLabel = agent.role ?? "domain"
  const modelTypeLabel = agent.agent_model.model_type

  return (
    <header className="flex flex-col gap-4">
      <div className="flex flex-row flex-wrap items-start justify-between gap-4">
        <div className="min-w-0 flex-1 space-y-2.5">
          <div className="flex flex-row flex-wrap items-center gap-3">
            <h1 className="text-[25px] font-semibold leading-tight tracking-[-0.015em] text-foreground">
              {card.name}
            </h1>
            <div
              role="group"
              aria-label="Agent badges"
              className="flex flex-row flex-wrap items-center gap-2"
            >
              <DotPill
                label={roleLabel}
                dotClass="bg-lineage"
                className="border-lineage/40 bg-lineage/10 text-lineage"
              />
              <DotPill
                label={modelTypeLabel}
                dotClass="bg-running"
                className="border-running/40 bg-running/10 text-llm"
              />
            </div>
          </div>

          {card.description ? (
            <p className="max-w-[560px] text-[13px] leading-[1.55] text-text-secondary">
              {card.description}
            </p>
          ) : null}

          <div className="flex flex-row flex-wrap items-center gap-3 text-[12.5px] text-text-tertiary">
            <span className="font-mono break-all">{agent.agent_id}</span>
            <CopyChip value={agent.agent_id} />
            <span className="text-text-muted">·</span>
            <span>created {formatCreated(agent.created_at)}</span>
          </div>
        </div>

        <button
          type="button"
          onClick={() => exportAgentModel(agent)}
          className="inline-flex h-[38px] items-center gap-2 rounded-md border border-border-strong bg-surface px-4 text-[13px] text-text-secondary transition-colors hover:border-primary hover:text-foreground"
        >
          <Download className="h-[15px] w-[15px]" />
          Export JSON
        </button>
      </div>
    </header>
  )
}
